import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { endpoints, type Access, type AccessPerson } from "../lib/api";
import { Initials, PeopleField } from "./PeoplePicker";

const REASONS: Record<AccessPerson["access"], string> = {
  organizer: "Organizer",
  bot: "Sent the bot",
  invited: "Invited",
  shared: "Shared",
  project: "Through a project",
};

/** Share a recorded meeting with colleagues: type a name or an email, pick
 * from the people who use the app, and see who can already open it. Shared
 * people see the recording, transcript and minutes; nobody's Personalized
 * details or chat. Drawn at the top of the page (a portal): the meeting page
 * is a CSS container, which would trap a fixed overlay inside it. */
export function ShareDialog({ sessionId, title, csrfToken, onClose }: {
  sessionId: string;
  title: string;
  csrfToken: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const key = ["access", sessionId];
  const access = useQuery({ queryKey: key, queryFn: () => endpoints.access(sessionId) });
  const [chosen, setChosen] = useState<string[]>([]);

  const updated = (next: Access) => queryClient.setQueryData(key, next);
  const share = useMutation({
    mutationFn: (emails: string[]) => endpoints.share(sessionId, emails, csrfToken),
    onSuccess: (next) => {
      updated(next);
      setChosen([]);
    },
  });
  const unshare = useMutation({
    mutationFn: (email: string) => endpoints.unshare(sessionId, email, csrfToken),
    onSuccess: updated,
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const people = useMemo(() => access.data?.people ?? [], [access.data]);
  const taken = useMemo(() => new Set(people.map((p) => p.email)), [people]);

  const error = (share.error ?? unshare.error ?? access.error) as Error | null;
  return createPortal(
    <div className="share-scrim" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="share-dialog" role="dialog" aria-modal="true" aria-labelledby="share-title">
        <div className="share-head">
          <h2 id="share-title">Share “{title}”</h2>
          <button className="icon-button" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="share-add">
          <PeopleField
            chosen={chosen}
            onChange={setChosen}
            taken={taken}
            domain={access.data?.domain ?? ""}
            onSubmit={() => share.mutate(chosen)}
            autoFocus
            label="Add people by name or email"
          />
          <button className="btn-primary" disabled={!chosen.length || share.isPending} onClick={() => share.mutate(chosen)}>
            {share.isPending ? "Sharing…" : "Share"}
          </button>
        </div>
        <p className="share-note">
          They'll see the recording, transcript and minutes, and can share it on. Personalized details and chat stay private.
        </p>
        {error && <div className="error">{error.message}</div>}

        <p className="share-label">People with access</p>
        {access.isLoading ? (
          <p className="share-note">Loading…</p>
        ) : (
          <ul className="share-people">
            {people.map((person) => (
              <li key={person.email}>
                <Initials name={person.name ?? person.email} />
                <span className="share-who">
                  <strong>
                    {person.name ?? person.email}
                    {person.you && <span className="share-you"> (you)</span>}
                  </strong>
                  <small>
                    {person.name ? `${person.email} · ` : ""}
                    {person.access === "shared" && person.shared_by
                      ? `Shared by ${person.shared_by}`
                      : person.access === "project" && person.project
                        ? `Through ${person.project}`
                        : REASONS[person.access]}
                    {!person.name && " · hasn't signed in yet"}
                  </small>
                </span>
                {person.can_remove && (
                  <button
                    className="btn-quiet danger"
                    disabled={unshare.isPending}
                    onClick={() => unshare.mutate(person.email)}
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        <p className="share-note">Admins can see every meeting.</p>
      </div>
    </div>,
    document.body,
  );
}
